import {
  BookOpen,
  CalendarClock,
  ChartColumn,
  ChevronRight,
  ClipboardList,
  Clock,
  DatabaseBackup,
  HardDrive,
  Inbox,
  Megaphone,
  MessageSquareHeart,
  MessageSquareText,
  PanelLeftClose,
  PanelLeftOpen,
  QrCode,
  ScrollText,
  Users,
  type LucideIcon,
} from 'lucide-react';
import { useState } from 'react';
import { Link, NavLink } from 'react-router';
import { useQueueCounts } from '../lib/it.ts';
import { usePageTitle } from '../lib/navigation.ts';
import { useSession } from '../lib/session.ts';

interface Item {
  to: string;
  label: string;
  icon: LucideIcon;
}

interface Section {
  title: string;
  items: Item[];
}

/** Every IT page, grouped the same way in the desktop sidebar and the phone "More" page. */
function sections(isAdmin: boolean): Section[] {
  const all: Section[] = [
    {
      title: 'Work',
      items: [
        { to: '/queue', label: 'Queue', icon: ClipboardList },
        { to: '/dashboard', label: 'Dashboard', icon: ChartColumn },
        { to: '/ratings', label: 'Feedback', icon: MessageSquareHeart },
        { to: '/tickets', label: 'My own tickets', icon: Inbox },
      ],
    },
    {
      title: 'Assets',
      items: [
        { to: '/equipment', label: 'Equipment', icon: HardDrive },
        { to: '/maintenance', label: 'Maintenance', icon: CalendarClock },
      ],
    },
    {
      title: 'Content',
      items: [
        { to: '/help', label: 'Knowledge base', icon: BookOpen },
        { to: '/replies', label: 'Saved replies', icon: MessageSquareText },
        { to: '/announcements', label: 'Announcements', icon: Megaphone },
      ],
    },
  ];
  if (isAdmin) {
    all.push({
      title: 'Admin',
      items: [
        { to: '/admin/users', label: 'Users', icon: Users },
        { to: '/admin/office', label: 'Office hours', icon: Clock },
        { to: '/admin/system', label: 'System & backups', icon: DatabaseBackup },
        { to: '/admin/audit', label: 'Audit log', icon: ScrollText },
        { to: '/admin/launch', label: 'Launch kit', icon: QrCode },
      ],
    });
  }
  return all;
}

const COLLAPSED_KEY = 'helpdesk.sidebarCollapsed';

function readCollapsed() {
  try {
    return localStorage.getItem(COLLAPSED_KEY) === '1';
  } catch {
    return false;
  }
}

/** Open tickets on the Queue link; red when some are overdue. */
function QueueBadge({ collapsed }: { collapsed: boolean }) {
  const counts = useQueueCounts().data;
  if (!counts) return null;
  const overdue = counts.overdue > 0;
  const n = overdue ? counts.overdue : counts.active;
  if (n === 0) return null;
  const label = overdue ? `${n} overdue` : `${n} open`;
  return (
    <span
      title={label}
      className={`rounded-full px-1.5 text-xs leading-5 font-semibold ${
        overdue ? 'bg-danger text-white' : 'bg-surface-2 text-muted'
      } ${collapsed ? 'absolute -top-1 -right-1 min-w-5 text-center text-[10px] leading-4' : 'ml-auto'}`}
    >
      <span className="sr-only">{label}</span>
      <span aria-hidden>{n}</span>
    </span>
  );
}

/**
 * Desktop and tablet: every IT page one click away, grouped, with the
 * current page highlighted. Collapses to icons; the choice is remembered.
 */
export function ItSidebar() {
  const { isAdmin } = useSession();
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const toggle = () => {
    setCollapsed((c) => {
      try {
        localStorage.setItem(COLLAPSED_KEY, c ? '0' : '1');
      } catch {
        // Not remembered in private browsing.
      }
      return !c;
    });
  };

  return (
    <aside
      aria-label="IT pages"
      className={`sticky top-16 hidden h-[calc(100dvh-4rem)] shrink-0 flex-col border-r border-line bg-surface md:flex ${
        collapsed ? 'w-16' : 'w-60'
      }`}
    >
      <nav className="flex-1 overflow-y-auto px-2 py-4">
        {sections(isAdmin).map((s) => (
          <div key={s.title} className="mb-4">
            {collapsed ? (
              <div className="mx-2 mb-2 border-t border-line" aria-hidden />
            ) : (
              <p className="mb-1 px-3 text-[11px] font-semibold tracking-wider text-muted uppercase">
                {s.title}
              </p>
            )}
            <ul className="space-y-0.5">
              {s.items.map(({ to, label, icon: Icon }) => (
                <li key={to}>
                  <NavLink
                    to={to}
                    end={to === '/tickets'}
                    title={collapsed ? label : undefined}
                    className={({ isActive }) =>
                      `relative flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                        collapsed ? 'justify-center' : ''
                      } ${
                        isActive
                          ? 'bg-brand-soft text-brand-ink'
                          : 'text-muted hover:bg-surface-2 hover:text-text'
                      }`
                    }
                  >
                    <Icon className="size-[18px] shrink-0" aria-hidden />
                    <span className={collapsed ? 'sr-only' : 'truncate'}>{label}</span>
                    {to === '/queue' && <QueueBadge collapsed={collapsed} />}
                  </NavLink>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </nav>
      <button
        type="button"
        onClick={toggle}
        aria-label={collapsed ? 'Expand the sidebar' : 'Collapse the sidebar'}
        title={collapsed ? 'Expand' : 'Collapse'}
        className={`m-2 flex items-center gap-3 rounded-lg px-3 py-2 text-sm text-muted hover:bg-surface-2 hover:text-text ${
          collapsed ? 'justify-center' : ''
        }`}
      >
        {collapsed ? (
          <PanelLeftOpen className="size-[18px]" aria-hidden />
        ) : (
          <>
            <PanelLeftClose className="size-[18px]" aria-hidden />
            Collapse
          </>
        )}
      </button>
    </aside>
  );
}

/** Phones: the "More" tab. The same sections as the sidebar, as a page so Back works. */
export function MorePage() {
  const { isAdmin } = useSession();
  usePageTitle('More');
  return (
    <div className="mx-auto max-w-lg space-y-6">
      <h1 className="text-2xl font-bold tracking-tight">More</h1>
      {sections(isAdmin).map((s) => (
        <section key={s.title}>
          <h2 className="mb-2 px-1 text-xs font-semibold tracking-wider text-muted uppercase">{s.title}</h2>
          <ul className="divide-y divide-line overflow-hidden rounded-xl border border-line bg-surface shadow-card">
            {s.items.map(({ to, label, icon: Icon }) => (
              <li key={to}>
                <Link to={to} className="flex items-center gap-3 px-4 py-3.5 hover:bg-surface-2">
                  <Icon className="size-5 text-muted" aria-hidden />
                  <span className="flex-1 font-medium">{label}</span>
                  <ChevronRight className="size-4 text-muted" aria-hidden />
                </Link>
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}
